import Card from '@/components/Card';
import { Input } from '@/components/Controls/Input';
import { Datatable } from '@/components/Datatable/Datatable';
import { useEffect, useState } from 'react';
import { FieldValues } from 'react-hook-form';
import { TableViewProps } from '@/type/listing.types';
import { useListAllDistributors, useListAllGroups } from '@/hooks/group.hooks';
import StructureFilter from '@/components/Custom/Filter/StructureFilter';
import { mapFilterSelect } from '@/utils/map.utils';
import { useStructureFilter } from '@/hooks/filters/structure-filter.hooks';
import { TableTitle } from '@/components/Header/TableTitle';
import Loader from '@/components/Loading/Loader';
import { useParams } from 'react-router-dom';
import { RoleName } from '@/api/entities/user-role-type';
import { useAuth, useUserRole } from '@/hooks/auth.hooks';
import { useListAllBranches } from '@/hooks/branch.hooks';
import { StructureType } from '@/entities/structure/common';
import { ClearButton } from '@/components/Custom/Button/ClearButton';
import { usePageSize } from '@/hooks/page-size.hooks';
import { STRUCTURE_TIPS } from '@/docs/tips/structure';
import { RichSelect } from '@/components/Controls/RichSelect';
import {
  AdvancedFilterPanel,
  AdvancedFilterToggle,
} from '@/components/Custom/Filter/FilterKit';
import { getUsageFilterOptions } from '@/components/Custom/Usage';

export const CompanyTableView = <TSchema extends FieldValues>({
  t,
  useGetAllData,
  onGetTotalActive,
  tableHeaders: TableHeaders,
  tableData: TableData,
  pendingDeactivate,
  onDeactivate,
}: TableViewProps<TSchema>) => {
  const { groupId } = useParams();
  const { primaryRole, loggedInUser } = useAuth();
  const { isAdmin, isGroupManager } = useUserRole();
  const [totalPages, setTotalPages] = useState<number>();
  const [currentPage, setCurrentPage] = useState<number>(1);
  const { pageSize: limit, setPageSize } = usePageSize();
  const [showAdvancedFilters, setShowAdvancedFilters] = useState(false);
  const [sortColumn, setSortColumn] = useState<string | null>(null);
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('asc');

  /**
   * === Filter Section ===
   */
  const {
    debouncedFilter,
    setDebouncedFilter,
    filtersEmpty,
    clearFilters,
    key,
    type,
    searchByKey,
    searchByTypes,
    startDate,
    endDate,
    isWhiteMark,
    distributorId,
    groupId: filterGroupId,
    companyId,
    branchId,
    searchBeginDate,
    searchEndDate,
    searchWhiteMark,
    searchByDistributor,
    searchByGroup,
    searchByCompany,
    searchByBranch,
  } = useStructureFilter({
    structureType: StructureType.COMPANY,
    setCurrentPage,
  });

  useEffect(() => {
    setDebouncedFilter((prev) => ({
      ...prev,
      role: primaryRole !== RoleName.ADMIN ? primaryRole : undefined,
      userId: loggedInUser?.id,
      groupId,
    }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [primaryRole, loggedInUser?.id, groupId]);
  const advancedFiltersCount = [
    startDate || endDate,
    isWhiteMark === 'true',
    distributorId,
    filterGroupId || groupId,
    branchId,
  ].filter(Boolean).length;
  /**
   * === END OF Filter Section ===
   */

  /**
   * === Data Fetching section ===
   */
  const {
    data: allData,
    isLoading: isLoadingAllData,
    isPending: isPendingAllData,
  } = useGetAllData(
    debouncedFilter,
    currentPage,
    limit,
    sortColumn,
    sortDirection,
  );

  const { data: allDistributors, isLoading: isLoadingAllDistributors } =
    useListAllDistributors();
  const { data: allGroups, isLoading: isLoadingAllGroups } = useListAllGroups({
    distributorId: debouncedFilter?.distributorId,
  });
  const { data: allBranches, isLoading: isLoadingAllBranches } =
    useListAllBranches({
      distributorId: debouncedFilter?.distributorId,
      groupId: debouncedFilter?.groupId,
      companyId: debouncedFilter?.companyId,
    });
  /**
   * === END OF Data Fetching section ===
   */

  /**
   * === Pagination Section ===
   */
  useEffect(() => {
    setTotalPages(allData?.data?.totalPages);
    onGetTotalActive?.(allData?.data?.['total']);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [allData]);
  /**
   * === END OF Pagination Section ===
   */

  /**
   * === Sorting Section ===
   */
  const handleSort = (column: string) => {
    setSortColumn(column);
    setSortDirection((prev) => (prev === 'asc' ? 'desc' : 'asc'));
  };
  /**
   * === END OF Sorting Section ===
   */

  return (
    <>
      <Card bodyClassName="flex flex-col gap-5">
        <div className="flex flex-wrap justify-between gap-3.5">
          <div className="flex gap-3.5 max-1.5xl:w-full max-1.5xl:justify-between">
            <TableTitle
              textClassName="mt-1.5"
              text={t('common:companies')}
              infoTip={STRUCTURE_TIPS.companiesTable}
            />
            <div className="hidden max-md:block">
              <ClearButton disabled={filtersEmpty} onClick={clearFilters} />
            </div>
          </div>
          <div className="flex flex-wrap gap-3.5 ml-auto max-1.5xl:ml-0 max-1.5xl:w-full">
            <div className="flex items-center flex-1 gap-3.5 min-w-[300px] max-1.5xl:min-w-0 max-1.5xl:basis-full">
              <Input
                placeholder={t('common:searchCompany')}
                type="text"
                name="search"
                autoComplete="off"
                leftIcon={
                  <i className="ki-outline ki-magnifier text-lg text-gray-8990A8" />
                }
                inputWrapperClassName="w-full"
                inputClassName="h-[36px] bg-gray-FCFCFC dark-mode:bg-coal-500"
                value={key}
                onChange={(e) => searchByKey(e.target.value)}
              />
              <div className="hidden max-1.5xl:block max-md:hidden">
                <ClearButton disabled={filtersEmpty} onClick={clearFilters} />
              </div>
            </div>
            <RichSelect
              containerClassName="w-auto min-w-[210px] max-1.5xl:min-w-0 max-sm:basis-full"
              placeholder={t('common:allUsages')}
              aria-label={t('common:usage')}
              options={getUsageFilterOptions(t)}
              value={type === '0' ? '' : type}
              onValueChange={searchByTypes}
            />
            <div className="flex justify-end max-sm:w-full">
              <AdvancedFilterToggle
                open={showAdvancedFilters}
                activeCount={advancedFiltersCount}
                onToggle={() => setShowAdvancedFilters((prev) => !prev)}
              />
            </div>
            <div className="max-1.5xl:hidden">
              <ClearButton disabled={filtersEmpty} onClick={clearFilters} />
            </div>
          </div>
        </div>
        <AdvancedFilterPanel open={showAdvancedFilters}>
          <StructureFilter
            t={t}
            isLoadingAllDistributors={isLoadingAllDistributors}
            isLoadingAllGroups={isLoadingAllGroups}
            isLoadingAllBranches={isLoadingAllBranches}
            distributorOptions={mapFilterSelect(allDistributors?.data)}
            groupOptions={mapFilterSelect(allGroups?.data)}
            branchOptions={mapFilterSelect(allBranches?.data)}
            displayDistributorFilter={isAdmin || isGroupManager}
            displayGroupFilter={isAdmin || isGroupManager}
            displayBranchFilter
            startDate={startDate}
            endDate={endDate}
            isWhiteMark={isWhiteMark}
            distributorId={distributorId}
            groupId={filterGroupId || groupId}
            companyId={companyId}
            branchId={branchId}
            searchBeginDate={searchBeginDate}
            searchEndDate={searchEndDate}
            searchWhiteMark={searchWhiteMark}
            searchByDistributor={searchByDistributor}
            searchByGroup={searchByGroup}
            searchByCompany={searchByCompany}
            searchByBranch={searchByBranch}
          />
        </AdvancedFilterPanel>
        <div>
          <Datatable
            t={t}
            headers={<TableHeaders t={t} onSort={handleSort} />}
            data={
              isLoadingAllData ? (
                <></>
              ) : !allData?.data?.data?.length ? null : (
                <TableData
                  t={t}
                  data={allData?.data?.data}
                  pendingDeactivate={pendingDeactivate}
                  onDeactivate={onDeactivate}
                />
              )
            }
            showPagination={allData?.data?.data?.length > 0 && totalPages > 1}
            totalPages={totalPages}
            currentPage={currentPage}
            onPageChange={(page) => setCurrentPage(page)}
            totalItems={isLoadingAllData ? undefined : allData?.data?.['total']}
            pageSize={limit}
            onPageSizeChange={setPageSize}
          />
        </div>
      </Card>
      {(isLoadingAllData || isPendingAllData || pendingDeactivate) && (
        <Loader />
      )}
    </>
  );
};
